// src/pages/TaskDashboard/TaskDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi, Task, DashboardStats as DashboardStatsType } from '../../api/tasks';
import { useUserInfo } from '../../hooks/useUserInfo';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { Popup } from '../../components/Popup';

export function TaskDashboard() {
  const navigate = useNavigate();
  const { currentUser } = useUserInfo();
  const { showError, popup, closePopup } = usePermissionGuard();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<DashboardStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [myUpcomingTasks, setMyUpcomingTasks] = useState<Task[]>([]);
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get all tasks for stats
      const tasksResponse = await tasksApi.list({ page_size: 100 });
      const allTasks = tasksResponse.data.items || [];
      setTasks(allTasks);

      // Get dashboard stats from existing API
      const statsResponse = await tasksApi.getDashboardStats();
      setStats(statsResponse.data);

      // Get my tasks for upcoming deadlines
      const myTasksResponse = await tasksApi.getMyTasks(false);
      const upcoming = (myTasksResponse.data.items || [])
        .filter(t => t.due_date && new Date(t.due_date) > new Date())
        .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
        .slice(0, 5);
      setMyUpcomingTasks(upcoming);

      // Get recent tasks (last 5 created)
      const recent = allTasks
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
      setRecentTasks(recent);

    } catch (err: any) {
      console.error('Dashboard error:', err);
      showError('DASHBOARD_ERROR', err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusStats = () => {
    const statusCounts = {
      'TODO': tasks.filter(t => t.status === 'TODO').length,
      'IN_PROGRESS': tasks.filter(t => t.status === 'IN_PROGRESS').length,
      'IN_REVIEW': tasks.filter(t => t.status === 'IN_REVIEW').length,
      'BLOCKED': tasks.filter(t => t.status === 'BLOCKED').length,
      'DONE': tasks.filter(t => t.status === 'DONE').length,
      'CANCELLED': tasks.filter(t => t.status === 'CANCELLED').length,
    };
    const total = tasks.length;
    const completed = statusCounts.DONE;
    const progress = total > 0 ? (completed / total) * 100 : 0;
    return { statusCounts, total, completed, progress };
  };

  const handleStatusClick = (status: string) => {
    navigate(`/tasks/list?status=${status}`);
  };

  const handlePriorityClick = (priority: string) => {
    navigate(`/tasks/list?priority=${priority}`);
  };

  const statusStats = getStatusStats();
  const statusColors = {
    'TODO': '#6B7280',
    'IN_PROGRESS': '#2563EB',
    'IN_REVIEW': '#7C3AED',
    'BLOCKED': '#DC2626',
    'DONE': '#059669',
    'CANCELLED': '#6B7280',
  };

  const statusLabels = {
    'TODO': 'To Do',
    'IN_PROGRESS': 'In Progress',
    'IN_REVIEW': 'In Review',
    'BLOCKED': 'Blocked',
    'DONE': 'Done',
    'CANCELLED': 'Cancelled',
  };

  const priorityColors = {
    URGENT: 'bg-red-500',
    HIGH: 'bg-orange-500',
    MEDIUM: 'bg-yellow-500',
    LOW: 'bg-green-500',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}!
              </h1>
              <p className="text-gray-500 mt-1">Here's what's happening with your tasks today</p>
            </div>
            {/* <button 
              onClick={() => navigate('/tasks/create')}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New Task
            </button> */}
          </div>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-blue-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/tasks/list')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Tasks</p>
                <p className="text-3xl font-bold text-gray-900">{statusStats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-blue-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${statusStats.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">{statusStats.completed} Completed</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-yellow-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleStatusClick('IN_PROGRESS')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-3xl font-bold text-yellow-600">{statusStats.statusCounts.IN_PROGRESS}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Actively being worked on</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-purple-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleStatusClick('IN_REVIEW')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Needs Review</p>
                <p className="text-3xl font-bold text-purple-600">{statusStats.statusCounts.IN_REVIEW}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Awaiting approval</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-red-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleStatusClick('BLOCKED')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Blocked</p>
                <p className="text-3xl font-bold text-red-600">{statusStats.statusCounts.BLOCKED}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Need attention</p>
          </div>
        </div>

        {/* Task Distribution Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Task Distribution</h3>
            </div>
            <div className="space-y-4">
              {Object.entries(statusStats.statusCounts).map(([status, count]) => (
                <div key={status} className="cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors" onClick={() => handleStatusClick(status)}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium" style={{ color: statusColors[status as keyof typeof statusColors] }}>
                      {statusLabels[status as keyof typeof statusLabels]}
                    </span>
                    <span className="text-gray-600">{count} tasks</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${statusStats.total > 0 ? (count / statusStats.total) * 100 : 0}%`,
                        backgroundColor: statusColors[status as keyof typeof statusColors]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Completion Rate</span>
                <span className="font-bold text-green-600">{Math.round(statusStats.progress)}%</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Open Tasks</span>
                <span className="font-bold text-blue-600">{statusStats.total - statusStats.statusCounts.DONE - statusStats.statusCounts.CANCELLED}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Overdue</span>
                <span className="font-bold text-red-600">{stats?.overdue || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Due Today</span>
                <span className="font-bold text-orange-600">{stats?.due_today || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Unassigned</span>
                <span className="font-bold text-purple-600">{stats?.unassigned || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Priority Breakdown and Upcoming Tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Priority Breakdown</h3>
            </div>
            <div className="space-y-3">
              {stats?.by_priority && Object.entries(stats.by_priority).map(([priority, count]) => (
                <div key={priority} className="cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors" onClick={() => handlePriorityClick(priority)}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{priority}</span>
                    <span className="text-gray-600">{count} tasks</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${priorityColors[priority as keyof typeof priorityColors]} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${(count / (stats?.open || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Your Upcoming Tasks</h3>
            </div>
            {myUpcomingTasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No upcoming tasks</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myUpcomingTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{task.title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                        task.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        task.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </div>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-green-500 h-1 rounded-full"
                        style={{ width: `${task.progress_percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Recent Tasks</h3>
            <button 
              onClick={() => navigate('/tasks/list')}
              className="ml-auto text-sm text-blue-600 hover:text-blue-700"
            >
              View all →
            </button>
          </div>
          {recentTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No recent tasks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    <div className="flex gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        task.status === 'TODO' ? 'bg-gray-100 text-gray-700' :
                        task.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                        task.status === 'IN_REVIEW' ? 'bg-purple-100 text-purple-700' :
                        task.status === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        task.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                        task.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        task.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                    </p>
                    <div className="w-24 mt-1 bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-green-500 h-1 rounded-full"
                        style={{ width: `${task.progress_percent}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Popup
        isOpen={popup.isOpen}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </>
  );
}

export default TaskDashboard;